/**
 * BulkDataUpdateModal - Modal for bulk updating data values for all data cards in a group
 *
 * Allows users to enter values for multiple data cards at once for a specific period.
 * Excludes linked cards as they get their data from the original card.
 *
 * Features:
 * - Manual entry in editable table
 * - Import from Excel template
 */
import { useState, useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { toast } from 'src/components/ui/toast';
import { usePostHog } from 'src/lib/posthog';
import { FloppyDiskIcon, TableIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import Modal from 'src/components/ui/Modal';
import Banner from 'src/components/ui/Banner';
import Button from 'src/components/ui/Button';
import PeriodPicker from './PeriodPicker';
import { isoYearForCalendarMonth, lastIsoWeekOfMonth } from '../utils/isoWeek';
import type { PeriodGrain } from '../utils/constants';
import type { UpdateCadence } from '../api/types';

function cadenceForGrain(grain: PeriodGrain): UpdateCadence {
  if (grain === 'week') return 'weekly';
  if (grain === 'quarter') return 'quarterly';
  return 'monthly';
}

const GRAIN_NOUN: Record<PeriodGrain, string> = {
  week: 'weekly',
  month: 'monthly',
  quarter: 'quarterly'
};
import Loading from 'src/components/ui/Loading';
import { useUpsertDataValue, queryKeys } from '../api/hooks';
import * as api from '../api/client';
import type { DataValue, DataValueType, ImportPreviewResponse, MetricNodeType } from '../api/types';
import type { ValidationError } from '../utils/validation';
import BulkDataEditableTable from './BulkDataEditableTable';
import ImportDataModal from './ImportDataModal';

interface DataCardInfo {
  id: string;
  name: string;
  /** Full path to the card (e.g., "Group A / Subgroup B / Card Name") */
  path?: string;
  dataValueType: DataValueType | null;
  currentValue: number | null;
  /** Node type – status cards accept only 1 (Yes) or 0 (No). */
  nodeType?: MetricNodeType;
  updateCadence?: UpdateCadence | null;
}

interface BulkDataUpdateModalProps {
  open: boolean;
  onClose: () => void;
  /** The node ID of the group/dashboard being bulk edited */
  nodeId: string;
  /** Root dashboard id, used for analytics */
  dashboardId?: string;
  /** Name of the group/dashboard for display */
  nodeName: string;
  dataCards: DataCardInfo[];
  initialYear: number;
  initialMonth: number;
  initialWeek?: number;
  initialGrain?: PeriodGrain;
  onSuccess?: () => void;
}

export default function BulkDataUpdateModal({
  open,
  onClose,
  nodeId,
  dashboardId,
  nodeName,
  dataCards,
  initialYear,
  initialMonth,
  initialWeek,
  initialGrain = 'month',
  onSuccess
}: BulkDataUpdateModalProps) {
  const posthog = usePostHog();
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedWeek, setSelectedWeek] = useState(
    initialWeek ?? lastIsoWeekOfMonth(initialYear, initialMonth).isoWeek
  );
  const [selectedGrain, setSelectedGrain] = useState<PeriodGrain>(initialGrain);
  const [values, setValues] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, ValidationError[]>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const upsertDataValueMutation = useUpsertDataValue();

  // Fetch data values for all cards when modal is open
  const dataValueQueries = useQueries({
    queries: dataCards.map((card) => ({
      queryKey: queryKeys.dataValues(card.id),
      queryFn: () => api.getDataValues(card.id),
      enabled: open
    }))
  });

  // Build a map of card ID -> data values for quick lookup
  const dataValuesMap = useMemo(() => {
    const map: Record<string, DataValue[]> = {};
    dataCards.forEach((card, index) => {
      const query = dataValueQueries[index];
      if (query.data?.data_values) {
        map[card.id] = query.data.data_values;
      }
    });
    return map;
  }, [dataCards, dataValueQueries]);

  // Get the current value for a card based on selected period
  const getCurrentValueForPeriod = (cardId: string): number | null => {
    const cardValues = dataValuesMap[cardId];
    if (!cardValues) return null;
    const card = dataCards.find((c) => c.id === cardId);
    if (card?.updateCadence === 'weekly') {
      const isoYear = isoYearForCalendarMonth(selectedYear, selectedMonth, selectedWeek);
      const match = cardValues.find((v) => v.period_year === isoYear && v.period_week === selectedWeek);
      return match?.value ?? null;
    }
    const match = cardValues.find((v) => v.period_year === selectedYear && v.period_month === selectedMonth);
    return match?.value ?? null;
  };

  // Check if any data is still loading
  const isLoadingData = dataValueQueries.some((q) => q.isLoading);

  // Reset values when modal opens
  useEffect(() => {
    if (open) {
      setValues({});
      setSelectedYear(initialYear);
      setSelectedMonth(initialMonth);
      setSelectedWeek(initialWeek ?? lastIsoWeekOfMonth(initialYear, initialMonth).isoWeek);
      setSelectedGrain(initialGrain);
    }
  }, [open, initialYear, initialMonth, initialWeek, initialGrain]);

  // Prepare table data with current values for the selected period
  const tableData = useMemo(() => {
    return dataCards.map((card) => ({
      ...card,
      currentValue: getCurrentValueForPeriod(card.id)
    }));
  }, [dataCards, dataValuesMap, selectedYear, selectedMonth, selectedWeek]);

  // Check if there are any validation errors
  const hasValidationErrors = Object.values(validationErrors).some((errors) => errors.length > 0);

  // Handle import preview - populate values from imported data
  const handleImportPreview = (data: ImportPreviewResponse) => {
    const newValues: Record<string, string> = { ...values };

    // Update selected period to match import
    setSelectedYear(data.period_year);
    setSelectedMonth(data.period_month);

    // Populate values from import
    for (const dataValue of data.data_values) {
      if (dataValue.value !== null && !dataValue.validation_error) {
        newValues[dataValue.node_id] = String(dataValue.value);
      }
    }

    setValues(newValues);
    toast.success(`Loaded ${data.cards_with_new_values} value${data.cards_with_new_values > 1 ? 's' : ''} from import`);
  };

  // Flattened, name-prefixed validation errors, so the inline panel and the
  // toast both reference the same list. Cells themselves are already
  // highlighted in BulkDataEditableTable; this surface gives reviewers the
  // whole picture at once instead of forcing scroll-and-hunt.
  const errorLines: string[] = dataCards
    .map((card) => {
      const errs = validationErrors[card.id];
      if (!errs?.length) return null;
      return `${card.name}: ${errs.map((e) => e.message).join(', ')}`;
    })
    .filter(Boolean) as string[];

  const handleSave = async () => {
    if (hasValidationErrors) {
      if (errorLines.length > 0) {
        toast.error(
          `${errorLines.length} row${errorLines.length === 1 ? '' : 's'} need attention — see the list above.`
        );
      }
      return;
    }

    const allowedCadence = cadenceForGrain(selectedGrain);
    const cardsToUpdate = dataCards.filter((card) => {
      const value = values[card.id];
      if (value === undefined || value === '') return false;
      return (card.updateCadence ?? 'monthly') === allowedCadence;
    });

    if (cardsToUpdate.length === 0) {
      toast.warning('No values to save. Enter at least one value.');
      return;
    }

    // Event #7: Bulk update saved
    posthog?.capture('assurance_bulk_update_saved', {
      dashboard_id: dashboardId,
      node_id: nodeId,
      cards_updated: cardsToUpdate.length,
      weekly_cards_updated: cardsToUpdate.filter((card) => card.updateCadence === 'weekly').length,
      period_grain: selectedGrain,
      period_year: selectedYear,
      period_month: selectedMonth,
      period_week: selectedGrain === 'week' ? selectedWeek : undefined,
      locked_cards_count: dataCards.filter((card) => (card.updateCadence ?? 'monthly') !== allowedCadence).length
    });

    setIsSaving(true);
    try {
      // Save all values
      const promises = cardsToUpdate.map((card) => {
        const numericValue = parseFloat(values[card.id]);
        if (isNaN(numericValue)) {
          throw new Error(`Invalid value for "${card.name}"`);
        }
        const isWeekly = card.updateCadence === 'weekly';
        return upsertDataValueMutation.mutateAsync({
          nodeId: card.id,
          data: isWeekly
            ? {
                period_year: isoYearForCalendarMonth(selectedYear, selectedMonth, selectedWeek),
                period_month: null,
                period_week: selectedWeek,
                value: numericValue
              }
            : {
                period_year: selectedYear,
                period_month: selectedMonth,
                value: numericValue
              }
        });
      });

      await Promise.all(promises);
      toast.success(`Updated ${cardsToUpdate.length} data card${cardsToUpdate.length > 1 ? 's' : ''}`);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const message = error?.message || 'Failed to save values. Please try again.';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const allowedCadence = cadenceForGrain(selectedGrain);
  const cardsWithValues = dataCards.filter((card) => {
    const value = values[card.id];
    return value !== undefined && value !== '' && (card.updateCadence ?? 'monthly') === allowedCadence;
  }).length;
  const editableCardCount = dataCards.filter((card) => (card.updateCadence ?? 'monthly') === allowedCadence).length;
  const lockedCardCount = dataCards.length - editableCardCount;

  return (
    <>
      <Modal open={open} onClose={onClose} title="Bulk Update Data" className="!max-w-2xl" disableBackdropClick>
        <div className="space-y-4">
          {/* Period selector */}
          <div className="pb-4 border-b border-border-default">
            <div className="min-w-0">
              <PeriodPicker
                label="Period"
                grain={selectedGrain}
                year={selectedYear}
                month={selectedMonth}
                week={selectedGrain === 'week' ? selectedWeek : null}
                onChange={(next) => {
                  if (next.grain !== selectedGrain) {
                    posthog?.capture('assurance_period_grain_changed', {
                      dashboard_id: dashboardId,
                      node_id: nodeId,
                      from_grain: selectedGrain,
                      to_grain: next.grain,
                      source: 'bulk_update'
                    });
                  } else if (next.grain === 'week') {
                    posthog?.capture('assurance_weekly_period_changed', {
                      dashboard_id: dashboardId,
                      node_id: nodeId,
                      period_year: next.year,
                      period_month: next.month,
                      period_week: next.week,
                      source: 'bulk_update'
                    });
                  }
                  setSelectedGrain(next.grain);
                  setSelectedYear(next.year);
                  setSelectedMonth(next.month);
                  setSelectedWeek(next.week ?? lastIsoWeekOfMonth(next.year, next.month).isoWeek);
                  if (next.grain !== selectedGrain) {
                    const allowed = cadenceForGrain(next.grain);
                    setValues((prev) => {
                      const nextValues = { ...prev };
                      for (const card of dataCards) {
                        if ((card.updateCadence ?? 'monthly') !== allowed) {
                          delete nextValues[card.id];
                        }
                      }
                      return nextValues;
                    });
                    setValidationErrors((prev) => {
                      const nextErrors = { ...prev };
                      for (const card of dataCards) {
                        if ((card.updateCadence ?? 'monthly') !== allowed) {
                          delete nextErrors[card.id];
                        }
                      }
                      return nextErrors;
                    });
                  }
                }}
              />
              <p className="text-xs text-content-subtle mt-2">
                Enter {GRAIN_NOUN[selectedGrain]} values only. Switch Week / Month / Quarter to edit cards with a
                different cadence.
              </p>
            </div>
          </div>

          {/* Data cards table */}
          {dataCards.length === 0 ? (
            <div className="text-center py-8 text-content-subtle">
              <TableIcon size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No data cards in this group.</p>
              <p className="text-xs mt-1">Add data cards to this group first, then use bulk update.</p>
            </div>
          ) : isLoadingData ? (
            <div className="py-8">
              <Loading text="Loading current values..." />
            </div>
          ) : (
            <>
              {errorLines.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800">
                    {errorLines.length} row{errorLines.length === 1 ? '' : 's'} need attention
                  </p>
                  <ul className="text-xs text-red-700 mt-1 list-disc list-inside max-h-40 overflow-y-auto">
                    {errorLines.map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}
              {lockedCardCount > 0 && (
                <Banner variant="info">
                  <span className="text-sm">Change period to edit locked data.</span>
                </Banner>
              )}
              <div className="max-h-80 overflow-y-auto">
                <BulkDataEditableTable
                  dataCards={tableData}
                  values={values}
                  onValuesChange={setValues}
                  validationErrors={validationErrors}
                  onValidationErrorsChange={setValidationErrors}
                  selectedGrain={selectedGrain}
                />
              </div>
            </>
          )}

          {dataCards.length > 0 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-content-subtle min-w-0">Import data via Excel template</p>
              <Button
                variant="outline"
                size="sm"
                icon={UploadSimpleIcon}
                onClick={() => {
                  posthog?.capture('assurance_bulk_update_import_clicked', {
                    dashboard_id: dashboardId,
                    node_id: nodeId,
                    period_grain: selectedGrain,
                    period_year: selectedYear,
                    period_month: selectedMonth,
                    period_week: selectedGrain === 'week' ? selectedWeek : undefined
                  });
                  setShowImportModal(true);
                }}
                className="shrink-0"
              >
                Import
              </Button>
            </div>
          )}

          {/* Footer with buttons */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-border-default">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={FloppyDiskIcon}
              onClick={handleSave}
              disabled={isSaving || cardsWithValues === 0 || hasValidationErrors}
              loading={isSaving}
            >
              Save All
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Data Modal */}
      <ImportDataModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        nodeId={nodeId}
        nodeName={nodeName}
        periodYear={selectedYear}
        periodMonth={selectedMonth}
        isBulk={true}
        onImportPreview={handleImportPreview}
        onImportComplete={() => {
          // Close the bulk update modal and trigger data refresh
          onSuccess?.();
          onClose();
        }}
      />
    </>
  );
}
